// 2026-06-25: Operations Map v3 — workflow-ordered Kanban (production flow, left → right).
// Layer 1: Floor KPIs overview
// Layer 2: Workflow flow strip (Raw → Machine Shop → Deburring → ... → Pack → Ship) with rollups
// Layer 3: One column per station, WC cards stacked within
// Layer 4 (drill-down): click a WC card → side panel with ops detail
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { getShopImage, getShopLabel, getShopColor } from '../../Shared/shopImages';
import { customerbasedshift, cleanCustomerId } from '../../Services/app/operatorservice';
import { getOperatorQueue } from '../../Services/app/workorderservice';

const BR = {
  orange: '#f97316', blue: '#2563eb', green: '#16a34a', red: '#dc2626',
  amber: '#f59e0b', dark: '#111827', muted: '#6b7280', border: '#e5e7eb',
  bg: '#f9fafb',
};

// The standard production workflow order — parts flow left → right through these stations.
const WORKFLOW_ORDER = [
  'MACHINE_SHOP',
  'DEBURRING',
  'SPECIAL_PROCESS',
  'ASSEMBLY',
  'INSPECTION',
  'PACKING',
  'OTHER',
];

function fmtMin(min) {
  if (min == null || isNaN(min)) return '—';
  const n = Number(min);
  if (n < 60) return n.toFixed(0) + 'm';
  return Math.floor(n / 60) + 'h ' + Math.round(n % 60) + 'm';
}

function KPITile({ icon, label, value, color, sub }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${BR.border}`, borderRadius: 8, padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
        <div style={{ fontSize: 9, fontWeight: 800, color: BR.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {label}
        </div>
        <div style={{ fontSize: 14, color: color || BR.muted, lineHeight: 1 }}>{icon}</div>
      </div>
      <div style={{ fontSize: 24, fontWeight: 900, color: color || BR.dark, marginTop: 6, lineHeight: 1 }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 10, color: BR.muted, marginTop: 3 }}>{sub}</div>}
    </div>
  );
}


// Phase 12 v13: ShopCard — one tile per shop, rolled-up across all its WCs.
// Click drills into the existing DrillDownPanel using a synthesised "virtual WC" carrying the totals.
function ShopCard({ shop, machineGroups = [], onClick }) {
  const wcs = shop.work_centers || [];
  if (wcs.length === 0) {
    return (
      <div style={{ padding: 16, background: '#fff', border: `1px dashed ${BR.border}`, borderRadius: 8,
                    textAlign: 'center', color: BR.muted, fontSize: 11 }}>
        No work centers
      </div>
    );
  }
  // Roll up counts across all WCs in this shop
  const c = wcs.reduce((acc, w) => {
    const wcCounts = w.counts || {};
    acc.ready      += wcCounts.ready      || 0;
    acc.running    += wcCounts.running    || 0;
    acc.on_hold    += wcCounts.on_hold    || 0;
    acc.done_today += wcCounts.done_today || 0;
    acc.wip_qty    += w.wip_pcs           || 0;
    acc.operators_active += w.active_operators || 0;
    acc.quality_holds    += w.quality_holds_open || 0;
    return acc;
  }, { ready: 0, running: 0, on_hold: 0, done_today: 0, wip_qty: 0, operators_active: 0, quality_holds: 0 });
  const avgProgress = Math.round(wcs.reduce((s, w) => s + (w.progress_pct || 0), 0) / Math.max(wcs.length, 1));

  const shopColor = getShopColor(shop.shop_type);
  const totalLoad = c.ready + c.running + c.on_hold;
  const completionPct = avgProgress;

  // Build a "virtual WC" for the drill panel: same shape as a real WC
  const virtualWc = {
    work_center_id: `shop:${shop.shop_type}`,
    code: shop.shop_type,
    name: getShopLabel(shop.shop_type),
    shop_type: shop.shop_type,
    counts: c,
    work_centers: wcs,  // include for drill panel
  };

  // Representative image: first non-empty WC's image, fallback to shop image
  const img = wcs[0]?.image_url || getShopImage(shop.shop_type);

  return (
    <div onClick={() => onClick(virtualWc)}
         style={{ background: '#fff', border: `1px solid ${BR.border}`, borderTop: `3px solid ${shopColor}`,
                  borderRadius: 8, marginBottom: 8, cursor: 'pointer', overflow: 'hidden',
                  transition: 'all 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}
         onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.10)';
                              e.currentTarget.style.transform = 'translateY(-1px)'; }}
         onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.04)';
                              e.currentTarget.style.transform = 'translateY(0)'; }}>
      {img && <img src={img} alt={shop.shop_type}
                   style={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }} />}
      <div style={{ padding: '8px 10px' }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: BR.dark }}>{getShopLabel(shop.shop_type)}</div>
        <div style={{ fontSize: 10, color: BR.muted, marginBottom: 6 }}>
          {(() => { const cells = (machineGroups || []).filter(g => g.shop_type === shop.shop_type); return cells.length ? `${cells.length} ${cells.length === 1 ? 'cell' : 'cells'}: ${cells.map(c => c.name).join(', ')}` : `${wcs.length} ${wcs.length === 1 ? 'cell' : 'cells'}`; })()}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4, marginBottom: 6 }}>
          <Stat label="●" value={c.ready} color={BR.muted} />
          <Stat label="▶" value={c.running} color={BR.green} />
          <Stat label="⏸" value={c.on_hold} color={BR.amber} />
          <Stat label="✓" value={c.done_today} color={shopColor} />
        </div>
        {(c.wip_qty > 0 || c.operators_active > 0) && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          fontSize: 11, color: BR.dark, padding: '4px 0', marginTop: 2,
                          borderTop: `1px dashed ${BR.border}` }}>
            {c.wip_qty > 0 && (
              <span><strong style={{ color: shopColor }}>{c.wip_qty}</strong> <span style={{ color: BR.muted }}>WIP pcs</span></span>
            )}
            {c.operators_active > 0 && (
              <span style={{ marginLeft: 'auto' }}>👤 <strong>{c.operators_active}</strong></span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ background: '#f9fafb', borderRadius: 4, padding: '4px 0', textAlign: 'center' }}>
      <div style={{ fontSize: 11, color, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 13, color: '#111827', fontWeight: 800 }}>{value}</div>
    </div>
  );
}

function WorkCenterCard({ wc, onClick }) {
  const shopColor = getShopColor(wc.shop_type);
  const shopImg = getShopImage(wc.shop_type);
  const hasHold = wc.quality_holds_open > 0;

  return (
    <button onClick={() => onClick(wc)}
            style={{
              background: '#fff', border: `1px solid ${hasHold ? BR.red : BR.border}`, borderRadius: 8,
              padding: 0, textAlign: 'left', cursor: 'pointer', width: '100%',
              overflow: 'hidden', position: 'relative',
              transition: 'transform 0.12s ease, box-shadow 0.12s ease',
              boxShadow: hasHold ? '0 0 0 3px rgba(220,38,38,0.16)' : '0 1px 3px rgba(0,0,0,0.05)',
              animation: hasHold ? 'wc-pulse 1.8s ease-in-out infinite' : 'none',
              marginBottom: 8,
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 10px rgba(0,0,0,0.08)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = hasHold ? '0 0 0 3px rgba(220,38,38,0.16)' : '0 1px 3px rgba(0,0,0,0.05)'; }}>
      {/* Image header (full-width thumbnail) */}
      <div style={{ position: 'relative', height: 70, overflow: 'hidden', borderBottom: `2px solid ${shopColor}` }}>
        <img src={shopImg} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        {hasHold && (
          <span style={{ position: 'absolute', top: 4, right: 4, background: BR.red, color: '#fff',
                          borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 800 }}>
            ⚠ {wc.quality_holds_open}
          </span>
        )}
      </div>

      <div style={{ padding: 8 }}>
        {/* Code + name (compact) */}
        <div style={{ fontSize: 12, fontWeight: 800, color: BR.dark, lineHeight: 1.1,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {wc.code}
        </div>
        <div style={{ fontSize: 10, color: BR.muted, marginTop: 2, marginBottom: 8,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {wc.name}
        </div>

        {/* Counts: compact 4-up */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 3, marginBottom: 8 }}>
          <CountChip n={wc.counts.ready}      color="#6b7280" symbol="●" tip="Ready" />
          <CountChip n={wc.counts.running}    color={BR.green} symbol="▶" tip="Running" />
          <CountChip n={wc.counts.on_hold}    color={BR.amber} symbol="⏸" tip="Hold" />
          <CountChip n={wc.counts.done_today} color={BR.green} symbol="✓" tip="Done" />
        </div>

        {/* WIP + operators */}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: BR.dark, marginBottom: 5 }}>
          <span title="Work-in-progress">📦 <strong>{(wc.wip_pcs ?? wc.counts?.wip_qty ?? 0)}</strong></span>
          <span title="Operators on shift">👤 <strong>{(wc.active_operators ?? wc.counts?.operators_active ?? 0)}</strong></span>
          <span style={{ color: BR.muted }}>{wc.progress_pct}%</span>
        </div>

        {/* Progress bar */}
        <div style={{ background: BR.border, borderRadius: 2, height: 3, overflow: 'hidden' }}>
          <div style={{ background: shopColor, height: '100%', width: `${wc.progress_pct}%`,
                         transition: 'width 0.3s ease' }} />
        </div>
      </div>
    </button>
  );
}

function CountChip({ n, color, symbol, tip }) {
  return (
    <div title={tip}
         style={{ background: n > 0 ? `${color}1a` : '#f9fafb',
                   border: `1px solid ${n > 0 ? color : BR.border}`,
                   borderRadius: 4, padding: '3px 0', textAlign: 'center' }}>
      <div style={{ fontSize: 11, fontWeight: 900, color: n > 0 ? color : '#9ca3af', lineHeight: 1 }}>
        {symbol} {n}
      </div>
    </div>
  );
}

function DrillDownPanel({ wc, customerId, machineGroups, onClose }) {
  const [ops, setOps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState('all');
  const [openCell, setOpenCell] = useState(null); // Phase 13: cell -> machine drill
  const scrollRef = React.useRef(null);

  useEffect(() => {
    if (!wc) return;
    let live = true;
    setLoading(true);
    setTab('all');
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    const isShopVirtual = typeof wc.work_center_id === 'string' && wc.work_center_id.startsWith('shop:');
    const filters = isShopVirtual ? { shop_type: wc.shop_type } : { work_center_id: wc.work_center_id };
    getOperatorQueue(customerId, filters)
      .then(r => { if (live) { setOps(r.operations || []); setErr(null); } })
      .catch(e => { if (live) setErr(e?.response?.data?.message || e.message); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [wc, customerId]);

  if (!wc) return null;
  const shopColor = getShopColor(wc.shop_type);
  const shopImg = getShopImage(wc.shop_type);
  const filteredOps = tab === 'all' ? ops
    : tab === 'ready' ? ops.filter(o => o.status === 'READY')
    : tab === 'running' ? ops.filter(o => o.status === 'IN_PROGRESS')
    : tab === 'hold' ? ops.filter(o => o.status === 'ON_HOLD')
    : ops;
  const totalActiveOps = wc.counts.ready + wc.counts.running + wc.counts.on_hold;

  return ReactDOM.createPortal((
    <div onClick={onClose}
         style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.45)', zIndex: 99999, display: 'flex', justifyContent: 'flex-end' }}>
      <div ref={scrollRef} onClick={e => e.stopPropagation()}
           style={{ width: 'min(820px, 96vw)', background: BR.bg, height: '100vh', overflow: 'auto', padding: 0,
                     boxShadow: '-8px 0 32px rgba(0,0,0,0.18)' }}>
        {/* Header — explicitly bold, colored background ensures visibility */}
        <div style={{ background: `linear-gradient(135deg, ${shopColor}f0 0%, ${shopColor}c0 100%)`,
                       padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 16,
                       minHeight: 110, position: 'sticky', top: 0, zIndex: 10,
                       boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <img src={shopImg} alt={getShopLabel(wc.shop_type)}
               style={{ width: 78, height: 78, borderRadius: 10, objectFit: 'cover',
                         border: '3px solid #fff', flexShrink: 0, background: '#fff',
                         boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 800, color: '#fff', textTransform: 'uppercase',
                              letterSpacing: 1, opacity: 0.95 }}>
                {getShopLabel(wc.shop_type)}
              </span>
              <span style={{ background: 'rgba(255,255,255,0.25)', color: '#fff', padding: '2px 8px', borderRadius: 4,
                              fontSize: 9, fontWeight: 800, letterSpacing: 0.6 }}>WIP VIEW</span>
            </div>
            <div style={{ fontSize: 24, fontWeight: 900, color: '#fff', marginTop: 4, lineHeight: 1.1,
                            textShadow: '0 1px 2px rgba(0,0,0,0.15)' }}>
              {wc.code}
            </div>
            <div style={{ fontSize: 13, color: '#fff', marginTop: 4, opacity: 0.92 }}>
              {wc.name}
            </div>
          </div>
          <button onClick={onClose}
                  title="Close"
                  style={{ background: 'rgba(255,255,255,0.25)', color: '#fff', border: '2px solid rgba(255,255,255,0.45)',
                            borderRadius: '50%', width: 40, height: 40, fontSize: 22, cursor: 'pointer', flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800,
                            lineHeight: 1 }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.4)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}>
            ×
          </button>
        </div>

        <div style={{ padding: 20 }}>
          {/* Big stat tiles */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
            <BigStat n={wc.counts.ready}      color="#6b7280" symbol="●" label="READY" />
            <BigStat n={wc.counts.running}    color={BR.green} symbol="▶" label="RUNNING" />
            <BigStat n={wc.counts.on_hold}    color={BR.amber} symbol="⏸" label="ON HOLD" />
            <BigStat n={wc.counts.done_today} color={BR.green} symbol="✓" label="DONE TODAY" />
          </div>

          {/* Context row */}
          <div style={{ background: '#fff', border: `1px solid ${BR.border}`, borderRadius: 8, padding: 14, marginBottom: 16,
                         display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            <ContextCell label="Machines" value={wc.machines?.join(' · ') || '—'} />
            <ContextCell label="WIP Quantity" value={`${(wc.wip_pcs ?? wc.counts?.wip_qty ?? 0)} pcs`}
                          highlight={(wc.wip_pcs ?? wc.counts?.wip_qty ?? 0) > 0 ? BR.dark : BR.muted}
                          sub={totalActiveOps > 0 ? `across ${totalActiveOps} active op${totalActiveOps !== 1 ? 's' : ''}` : 'no active ops'} />
            <ContextCell label="Active Operators" value={String((wc.active_operators ?? wc.counts?.operators_active ?? 0))}
                          highlight={(wc.active_operators ?? wc.counts?.operators_active ?? 0) > 0 ? BR.green : BR.muted} />
          </div>

          {(() => {
            // Show real Machine Groups (CELL-8, CELL-6, …) for THIS shop, with op counts derived from `ops` list.
            const cellsHere = (machineGroups || []).filter(g => g.shop_type === wc.shop_type);
            if (cellsHere.length === 0) return null;
            // Build a fast lookup: machine_name → set of cells it belongs to
            const machineToCells = {};
            cellsHere.forEach(g => (g.machines || []).forEach(m => {
              const name = typeof m === 'string' ? m : m?.name;
              if (!name) return;
              (machineToCells[name] = machineToCells[name] || []).push(g.name);
            }));
            // Tally ops per cell from the current ops array (loaded in this panel)
            const tallies = {};
            cellsHere.forEach(g => { tallies[g.name] = { ready: 0, running: 0, on_hold: 0 }; });
            ops.forEach(o => {
              const cs = machineToCells[o.machine_name] || [];
              cs.forEach(cellName => {
                const t = tallies[cellName];
                if (!t) return;
                if (o.status === 'READY')      t.ready++;
                else if (o.status === 'IN_PROGRESS') t.running++;
                else if (o.status === 'ON_HOLD')     t.on_hold++;
              });
            });
            return (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: BR.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                  Cells in this shop ({cellsHere.length})
                </div>
                <div style={{ display: 'grid', gap: 6 }}>
                  {cellsHere.map(g => {
                    const t = tallies[g.name] || {};
                    const machineCount = (g.machines || []).length;
                    const isOpen = openCell === g.name;
                    const machineNames = (g.machines || []).map(m => typeof m === 'string' ? m : m?.name).filter(Boolean);
                    // Per-machine op breakdown from current ops list
                    const opsByMachine = {};
                    machineNames.forEach(mn => { opsByMachine[mn] = []; });
                    ops.forEach(o => {
                      if (o.machine_name && opsByMachine[o.machine_name]) opsByMachine[o.machine_name].push(o);
                    });
                    return (
                      <div key={g.name}>
                        <div onClick={() => setOpenCell(isOpen ? null : g.name)}
                             style={{ background: isOpen ? '#fff' : '#f9fafb', border: `1px solid ${BR.border}`,
                                      borderLeft: `3px solid ${shopColor}`,
                                      borderRadius: isOpen ? '6px 6px 0 0' : 6, padding: '8px 10px', display: 'grid',
                                      gridTemplateColumns: '14px 1.5fr 50px 50px 50px 60px', gap: 8, alignItems: 'center',
                                      fontSize: 12, cursor: 'pointer', userSelect: 'none' }}>
                          <span style={{ fontSize: 10, color: BR.muted, transition: 'transform 0.15s',
                                          transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                          <div>
                            <div style={{ fontWeight: 700, color: BR.dark }}>{g.name}</div>
                            <div style={{ fontSize: 10, color: BR.muted }}>{machineCount} machine{machineCount !== 1 ? 's' : ''}</div>
                          </div>
                          <span title="Ready" style={{ textAlign: 'center' }}>● {t.ready || 0}</span>
                          <span title="Running" style={{ textAlign: 'center', color: BR.green }}>▶ {t.running || 0}</span>
                          <span title="On hold" style={{ textAlign: 'center', color: BR.amber }}>⏸ {t.on_hold || 0}</span>
                          <span style={{ fontWeight: 700, color: BR.dark, textAlign: 'right' }} title="Total ops here">{(t.ready||0)+(t.running||0)+(t.on_hold||0)} ops</span>
                        </div>
                        {isOpen && (
                          <div style={{ background: '#fff', border: `1px solid ${BR.border}`, borderTop: 'none',
                                          borderRadius: '0 0 6px 6px', padding: '6px 10px 8px' }}>
                            {machineNames.length === 0 ? (
                              <div style={{ fontSize: 11, color: BR.muted, padding: '6px 0', textAlign: 'center' }}>
                                No machines assigned to this cell
                              </div>
                            ) : machineNames.map(mn => {
                              const mOps = opsByMachine[mn] || [];
                              const running = mOps.find(o => o.status === 'IN_PROGRESS');
                              const ready = mOps.filter(o => o.status === 'READY');
                              const onHold = mOps.filter(o => o.status === 'ON_HOLD');
                              const stateColor = running ? BR.green : onHold.length ? BR.amber : ready.length ? '#6b7280' : '#d1d5db';
                              const stateLabel = running ? 'RUNNING' : onHold.length ? 'ON HOLD' : ready.length ? 'READY' : 'IDLE';
                              return (
                                <div key={mn}
                                     style={{ display: 'grid', gridTemplateColumns: '14px 1fr 80px 1fr', gap: 8,
                                              alignItems: 'center', padding: '6px 4px',
                                              borderTop: `1px dashed ${BR.border}`, fontSize: 11 }}>
                                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: stateColor,
                                                  display: 'inline-block' }} />
                                  <div>
                                    <div style={{ fontWeight: 700, color: BR.dark, fontFamily: 'monospace' }}>{mn}</div>
                                    {running && (
                                      <div style={{ fontSize: 10, color: BR.muted }}>
                                        {running.wo_number} · {running.op_seq} · {running.op_name}
                                        {running.claimed_by_operator_name && ` · by ${running.claimed_by_operator_name}`}
                                      </div>
                                    )}
                                    {!running && ready.length > 0 && (
                                      <div style={{ fontSize: 10, color: BR.muted }}>
                                        Next: {ready[0].wo_number} · {ready[0].op_seq}
                                      </div>
                                    )}
                                  </div>
                                  <span style={{ padding: '1px 6px', borderRadius: 3, background: stateColor,
                                                  color: '#fff', fontSize: 9, fontWeight: 800, textAlign: 'center', letterSpacing: 0.4 }}>
                                    {stateLabel}
                                  </span>
                                  <div style={{ fontSize: 10, color: BR.muted, textAlign: 'right' }}>
                                    {mOps.length > 0 ? `${mOps.length} op${mOps.length !== 1 ? 's' : ''}` : '—'}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 14, borderBottom: `1px solid ${BR.border}` }}>
            <TabBtn active={tab === 'all'} onClick={() => setTab('all')} label="All" count={ops.length} color={BR.dark} />
            <TabBtn active={tab === 'ready'} onClick={() => setTab('ready')} label="● Ready" count={ops.filter(o => o.status === 'READY').length} color="#6b7280" />
            <TabBtn active={tab === 'running'} onClick={() => setTab('running')} label="▶ Running" count={ops.filter(o => o.status === 'IN_PROGRESS').length} color={BR.green} />
            <TabBtn active={tab === 'hold'} onClick={() => setTab('hold')} label="⏸ Hold" count={ops.filter(o => o.status === 'ON_HOLD').length} color={BR.amber} />
          </div>

          {err && <div style={{ color: BR.red, padding: 10, background: '#fee2e2', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>{err}</div>}

          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: BR.muted, fontSize: 14 }}>Loading WIP…</div>
          ) : filteredOps.length === 0 ? (
            <EmptyState wc={wc} tab={tab} />
          ) : (
            <div>
              {filteredOps.map(op => <OpCard key={op.id} op={op} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  ), document.body);
}

function BigStat({ n, color, symbol, label }) {
  const active = n > 0;
  return (
    <div style={{ background: '#fff', border: `1.5px solid ${active ? color : BR.border}`, borderRadius: 8, padding: 14,
                   textAlign: 'center' }}>
      <div style={{ fontSize: 20, color: active ? color : '#9ca3af', lineHeight: 1, marginBottom: 4 }}>{symbol}</div>
      <div style={{ fontSize: 28, fontWeight: 900, color: active ? color : '#9ca3af', lineHeight: 1 }}>{n}</div>
      <div style={{ fontSize: 9, fontWeight: 800, color: active ? color : BR.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 4 }}>
        {label}
      </div>
    </div>
  );
}

function ContextCell({ label, value, highlight, sub }) {
  return (
    <div>
      <div style={{ fontSize: 9, fontWeight: 800, color: BR.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: highlight || BR.dark, marginTop: 3, wordBreak: 'break-word' }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: BR.muted, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function TabBtn({ active, onClick, label, count, color }) {
  return (
    <button onClick={onClick}
            style={{ padding: '8px 14px', fontSize: 12, fontWeight: 700,
                      background: active ? '#fff' : 'transparent', color: active ? color : BR.muted,
                      border: 'none', borderBottom: `3px solid ${active ? color : 'transparent'}`,
                      cursor: 'pointer', marginBottom: -1 }}>
      {label} <span style={{ background: active ? `${color}1a` : BR.border, color: active ? color : BR.dark,
                              borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 800, marginLeft: 4 }}>{count}</span>
    </button>
  );
}

function OpCard({ op }) {
  const statusColor = op.status === 'IN_PROGRESS' ? BR.green
    : op.status === 'ON_HOLD' ? BR.amber
    : '#6b7280';
  const prioColor = op.priority === 'URGENT' ? BR.red : op.priority === 'HIGH' ? BR.orange
    : op.priority === 'NORMAL' ? BR.blue : BR.muted;
  return (
    <div style={{ background: '#fff', border: `1px solid ${BR.border}`, borderRadius: 8, padding: 14, marginBottom: 10,
                   borderLeft: `4px solid ${statusColor}` }}>
      {/* Top row: title + badges (badges inline at right) */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'monospace', background: '#f3f4f6', padding: '2px 7px', borderRadius: 3,
                            fontSize: 12, fontWeight: 800, color: BR.dark }}>{op.op_seq}</span>
            <span style={{ fontSize: 14, fontWeight: 800, color: BR.dark }}>{op.op_name}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          <span style={{ background: prioColor, color: '#fff', borderRadius: 4, padding: '3px 8px', fontSize: 10, fontWeight: 800 }}>
            {op.priority}
          </span>
          <span style={{ background: `${statusColor}1a`, color: statusColor, border: `1px solid ${statusColor}`,
                          borderRadius: 4, padding: '2px 7px', fontSize: 10, fontWeight: 800 }}>
            {op.status === 'IN_PROGRESS' ? 'RUNNING' : op.status === 'ON_HOLD' ? 'HOLD' : op.status}
          </span>
        </div>
      </div>

      {/* WO row */}
      <div style={{ fontSize: 12, color: BR.muted }}>
        <strong style={{ fontFamily: 'monospace', color: BR.dark }}>{op.wo_number}</strong>
        {' · '}{op.part_name || op.part_number} <strong style={{ color: BR.dark }}>· qty {op.planned_qty}</strong>
      </div>

      {/* Meta row */}
      <div style={{ display: 'flex', gap: 14, marginTop: 8, fontSize: 11, color: BR.muted, flexWrap: 'wrap' }}>
        {op.machine_name && <span>🛠 <strong style={{ color: BR.dark }}>{op.machine_name}</strong></span>}
        {op.claimed_by_operator_name && <span>👤 <strong style={{ color: BR.dark }}>{op.claimed_by_operator_name}</strong></span>}
        {op.due_date && <span>📅 Due <strong style={{ color: BR.dark }}>{new Date(op.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</strong></span>}
      </div>
    </div>
  );
}

function EmptyState({ wc, tab }) {
  const messages = {
    all: { icon: '📭', title: 'No WIP at this work center',
            sub: wc.counts.done_today > 0
              ? `${wc.counts.done_today} op${wc.counts.done_today !== 1 ? 's were' : ' was'} completed here today. Station is currently idle.`
              : 'Station is idle. No ops queued, running, or held.' },
    ready: { icon: '✅', title: 'Nothing waiting', sub: 'No ops are READY to start here right now.' },
    running: { icon: '⏸', title: 'Nothing running', sub: 'No ops are currently IN PROGRESS at this station.' },
    hold: { icon: '✅', title: 'No quality holds', sub: 'No ops are ON HOLD. Quality is clean here.' },
  };
  const m = messages[tab] || messages.all;
  return (
    <div style={{ padding: 50, textAlign: 'center', background: '#fff', border: `1px dashed ${BR.border}`,
                   borderRadius: 8 }}>
      <div style={{ fontSize: 48, marginBottom: 10 }}>{m.icon}</div>
      <div style={{ fontSize: 15, fontWeight: 800, color: BR.dark }}>{m.title}</div>
      <div style={{ fontSize: 12, color: BR.muted, marginTop: 8, maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>{m.sub}</div>
    </div>
  );
}




// Column header with shop image + workflow position + rollup
function StationHeader({ shopType, position, totalSteps, wcs }) {
  const color = getShopColor(shopType);
  const img = getShopImage(shopType);
  const totalRunning = wcs.reduce((a, w) => a + w.counts.running, 0);
  const totalReady = wcs.reduce((a, w) => a + w.counts.ready, 0);
  const totalWip = wcs.reduce((a, w) => a + w.wip_pcs, 0);
  const holds = wcs.reduce((a, w) => a + w.quality_holds_open, 0);

  return (
    <div style={{ background: '#fff', border: `1px solid ${BR.border}`, borderRadius: 8, padding: 0,
                   overflow: 'hidden', marginBottom: 8 }}>
      <div style={{ background: color, padding: '6px 10px', color: '#fff',
                     display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ background: 'rgba(255,255,255,0.25)', borderRadius: 12,
                        width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 800 }}>
          {position}
        </div>
        <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, flex: 1 }}>
          {getShopLabel(shopType)}
        </div>
        {holds > 0 && (
          <span style={{ background: BR.red, color: '#fff', borderRadius: 10, padding: '1px 6px',
                          fontSize: 9, fontWeight: 800 }}>⚠ {holds}</span>
        )}
      </div>
      <div style={{ padding: '6px 10px', display: 'flex', justifyContent: 'space-between', fontSize: 10, color: BR.dark }}>
        <span>● {totalReady}</span>
        <span style={{ color: BR.green, fontWeight: 700 }}>▶ {totalRunning}</span>
        <span>📦 {totalWip}</span>
        <span style={{ color: BR.muted }}>{wcs.length} WC{wcs.length !== 1 ? 's' : ''}</span>
      </div>
    </div>
  );
}

export default function OperationsMap() {
  const customerId = cleanCustomerId(localStorage.getItem('CustomerID') || window._env_.CUSTOMER_ID);
  const [machineGroupsAttr, setMachineGroupsAttr] = useState([]);

  // Fetch machine groups (cells) so the drill panel can show cell-level rollups.
  React.useEffect(() => {
    if (!customerId) return;
    customerbasedshift(customerId, 'machinegroups')
      .then(r => setMachineGroupsAttr(Array.isArray(r?.[0]?.value) ? r[0].value : []))
      .catch(() => setMachineGroupsAttr([]));
  }, [customerId]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [selectedWc, setSelectedWc] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const refresh = useCallback(async () => {
    if (!customerId) { setErr('No customer_id'); setLoading(false); return; }
    try {
      const r = await axios.get(`${window._env_.SERVER_URL2}api/workorder/operations-overview`,
                                 { params: { customer_id: customerId } });
      setData(r.data); setErr(null); setLastUpdated(new Date());
    } catch (e) { setErr(e?.response?.data?.message || e.message); }
    finally { setLoading(false); }
  }, [customerId]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 15000);
    return () => clearInterval(id);
  }, [refresh]);

  // Order shops by WORKFLOW (production flow), not alphabetical
  const orderedShops = useMemo(() => {
    if (!data?.shops) return [];
    const byType = {};
    for (const s of data.shops) byType[s.shop_type] = s;
    return WORKFLOW_ORDER
      .filter(t => byType[t])
      .map(t => byType[t]);
  }, [data]);

  if (loading && !data) return <div style={{ padding: 60, textAlign: 'center', color: BR.muted, fontSize: 14 }}>Loading operations map…</div>;
  if (err) return <div style={{ padding: 20, color: BR.red }}>⚠ {err}</div>;

  const summary = data?.summary || {};

  return (
    <div style={{ padding: 12, background: BR.bg, minHeight: '100vh' }}>
      <style>{`
        @keyframes wc-pulse {
          0%, 100% { box-shadow: 0 0 0 3px rgba(220,38,38,0.16); }
          50%      { box-shadow: 0 0 0 6px rgba(220,38,38,0.26); }
        }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 900, color: BR.dark, display: 'flex', alignItems: 'center', gap: 6 }}>
            📍 Operations Map
          </div>
          <div style={{ fontSize: 10, color: BR.muted }}>
            Production workflow · auto-refresh 15s
            {lastUpdated && <span> · {lastUpdated.toLocaleTimeString('en-IN')}</span>}
          </div>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <button onClick={refresh}
                  style={{ padding: '6px 14px', background: BR.orange, color: '#fff', border: 'none',
                            borderRadius: 5, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>↻ Refresh</button>
        </div>
      </div>

      {/* Floor KPI Overview */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8, marginBottom: 14 }}>
        <KPITile icon="●" label="WO Queue" value={summary.ready_ops}    color="#6b7280" sub="waiting" />
        <KPITile icon="▶" label="Running"  value={summary.running_ops}  color={BR.green} sub="in progress" />
        <KPITile icon="⏸" label="Holds"    value={summary.on_hold_ops}  color={summary.on_hold_ops > 0 ? BR.amber : BR.muted} sub="paused" />
        <KPITile icon="✓" label="Done"     value={summary.done_today}   color={BR.green} sub={`${summary.wos_completed_today} WOs closed`} />
        <KPITile icon="⚠" label="Q-Holds"  value={summary.quality_holds_open}
                  color={summary.quality_holds_open > 0 ? BR.red : BR.muted}
                  sub={summary.quality_holds_open > 0 ? 'review now' : 'all clear'} />
        <KPITile icon="👤" label="Workers" value={summary.operators_clocked_in || 0}
                  color={BR.dark} sub={`${fmtMin(summary.labour_min_today)} clocked today`} />
      </div>

      {/* Workflow flow strip — visual production flow */}
      <div style={{ background: '#fff', border: `1px solid ${BR.border}`, borderRadius: 8, padding: 10, marginBottom: 14 }}>
        <div style={{ fontSize: 9, fontWeight: 800, color: BR.muted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>
          Production Workflow
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, overflowX: 'auto' }}>
          <div style={{ fontSize: 11, color: BR.muted, fontStyle: 'italic', flexShrink: 0 }}>Raw →</div>
          {orderedShops.map((s, i) => {
            const color = getShopColor(s.shop_type);
            const running = s.work_centers.reduce((a, w) => a + w.counts.running, 0);
            return (
              <React.Fragment key={s.shop_type}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px',
                                border: `1.5px solid ${color}`, borderRadius: 20, background: `${color}11`,
                                flexShrink: 0 }}>
                  <span style={{ background: color, color: '#fff', width: 18, height: 18, borderRadius: '50%',
                                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: 10, fontWeight: 800 }}>
                    {i + 1}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 700, color }}>{getShopLabel(s.shop_type)}</span>
                  {running > 0 && (
                    <span style={{ background: BR.green, color: '#fff', borderRadius: 8, padding: '0 6px',
                                    fontSize: 9, fontWeight: 800 }}>▶{running}</span>
                  )}
                </div>
                {i < orderedShops.length - 1 && (
                  <span style={{ color: BR.muted, fontSize: 14, flexShrink: 0 }}>→</span>
                )}
              </React.Fragment>
            );
          })}
          <div style={{ fontSize: 11, color: BR.muted, fontStyle: 'italic', flexShrink: 0 }}>→ Ship</div>
        </div>
      </div>

      {/* Kanban-style station columns — one per shop, in workflow order */}
      {orderedShops.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: BR.muted, fontSize: 14,
                       background: '#fff', border: `1px solid ${BR.border}`, borderRadius: 8 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
          No work centers yet.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${orderedShops.length}, minmax(0, 1fr))`, gap: 10 }}>
          {orderedShops.map((shop, idx) => (
            <div key={shop.shop_type}>
              <StationHeader shopType={shop.shop_type} position={idx + 1} totalSteps={orderedShops.length} wcs={shop.work_centers} />
              <ShopCard shop={shop} machineGroups={machineGroupsAttr} onClick={(virtualWc) => setSelectedWc(virtualWc)} />
            </div>
          ))}
        </div>
      )}

      <DrillDownPanel wc={selectedWc} customerId={customerId} machineGroups={machineGroupsAttr} onClose={() => setSelectedWc(null)} />
    </div>
  );
}
